import {
  Alert,
  Box,
  Stack,
  TextField,
  Typography,
  Divider,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NewOrderTypes } from '../../../../common/enums/enums';
import { selectPersonId } from '../../../../redux/selectors/userDataSelectors';
import { getSelectorActionsBasedOnType } from '../helper';
import SpecialRequests from './components/SpecialRequests';

export default function Extras({
  showAlert = true,
  boxProps = { p: 4, px: 8 },
  autoFocus = true,
  orderType = NewOrderTypes.SIMPLE_ORDER,
  selectedVendorDepartment = true,
}) {
  const { t } = useTranslation('orders');
  const dispatch = useDispatch();
  const {
    addSpecialRequestAction,
    removeSpecialRequestAction,
    setAlternativeOwnerAction,
    specialRequestsSelector,
    choosenAlternativeOwner,
    alternateOwnerSelector,
    selectedCategoriesSelector,
  } = getSelectorActionsBasedOnType(orderType);
  const specialRequests = useSelector(specialRequestsSelector);
  const users = useSelector(alternateOwnerSelector);
  const currentPersonId = useSelector(selectPersonId);
  const selectedOwner = useSelector(choosenAlternativeOwner);
  const selectedCategories = useSelector(selectedCategoriesSelector);

  const handleChangeOwner = (event, value) => {
    if (value) {
      dispatch(
        setAlternativeOwnerAction({
          personId: value?.personId,
          label: value.label,
        })
      );
    }
  };

  return (
    <Stack
      justifyContent="space-between"
      height="100%"
      mb={{ xs: 25, md: 'unset' }}
    >
      <Box {...boxProps}>
        <Box>
          <Typography variant="h6" mb={3}>
            {t('newOrder.extras.title')}
          </Typography>
        </Box>
        {selectedCategories?.length > 1 && (
          <Box mb={3}>
            <Alert
              sx={{
                '& .MuiAlert-icon': {
                  color: theme => theme.palette.alert.color,
                },
                backgroundColor: theme => theme.palette.alert.background,
              }}
              severity={'info'}
            >
              {t('newOrder.multipleOrders.extras')}
            </Alert>
          </Box>
        )}
        <Box mb={3}>
          <Typography fontSize="small" mb={2}>
            {t('newOrder.extras.specialRequest')}:
          </Typography>
          <SpecialRequests
            specialRequests={specialRequests}
            autoFocus={autoFocus}
            onRemoveSpecialRequest={index =>
              dispatch(removeSpecialRequestAction(index))
            }
            onAddSpecialRequest={newSpecialRequest =>
              dispatch(addSpecialRequestAction(newSpecialRequest))
            }
          />
        </Box>
        <Divider />
        <Box width={{ xs: '100%', md: '50%', lg: '50%' }} mb={4} mt={4}>
          <Typography fontSize="small" mb={2}>
            {t('newOrder.alternativeOwner')}
          </Typography>

          <Autocomplete
            disabled={!selectedVendorDepartment}
            options={[{ label: 'None', personId: null }].concat(
              users
                ?.filter(user => currentPersonId !== user.personId)
                .map(user => ({
                  personId: user.personId,
                  label: [user?.firstName, user?.lastName]
                    .filter(x => x)
                    .join(' '),
                }))
            )}
            value={selectedOwner?.label !== 'None' ? selectedOwner.label : ''}
            onChange={handleChangeOwner}
            renderOption={(props, option) => {
              return (
                <li
                  {...props}
                  key={option.personId}
                  style={{
                    fontStyle: option.label === 'None' ? 'italic' : '',
                  }}
                >
                  {option.label}
                </li>
              );
            }}
            renderInput={params =>
              !selectedVendorDepartment ? (
                <Tooltip
                  title={
                    !selectedVendorDepartment
                      ? 'Please select Vendor/Department'
                      : undefined
                  }
                >
                  <TextField {...params} label={t('newOrder.steps.newOwner')} />
                </Tooltip>
              ) : (
                <TextField {...params} label={t('newOrder.steps.newOwner')} />
              )
            }
            size="small"
            sx={{
              backgroundColor: 'white',
            }}
            ListboxProps={{ style: { maxHeight: 190 } }}
          />
        </Box>
      </Box>
    </Stack>
  );
}
